import { useEffect, useRef, useState, useCallback, useMemo, useTransition } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  createColumnHelper,
  type SortingState,
  type ColumnFiltersState,
} from "@tanstack/react-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  AlertTriangle,
  PlusCircle,
  RefreshCw,
  ExternalLink,
  ArrowUpFromLine,
  ArrowDownToLine,
  Pencil,
  EyeOff,
  X,
  LayoutList,
  ImageOff,
  Copy,
  CloudDownload,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { toast } from "sonner";
import { api, type StoreProduct, type ImportConflict, type ShopifyCatalogProduct } from "@/lib/api";
import { useShopifyAdminURL } from "@/lib/useShopifyAdmin";
import { formatUsd, formatInt, cn } from "@/lib/utils";
import { SyncHealthStrip } from "./SyncHealthStrip";

// ── Status badge ─────────────────────────────────────────────────────────────
function ShopifyStatusBadge({ status }: { status: StoreProduct["shopify_status"] }) {
  if (!status) {
    return (
      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] py-0 px-1.5">
        not synced
      </Badge>
    );
  }
  const styles: Record<string, string> = {
    ACTIVE: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    DRAFT: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    ARCHIVED: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  };
  return (
    <Badge
      className={cn(
        "text-[10px] py-0 px-1.5",
        styles[status] ?? "bg-muted/30 text-muted-foreground",
      )}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </Badge>
  );
}

// ── Drift indicator ───────────────────────────────────────────────────────────
function DriftIndicator({ drift }: { drift: StoreProduct["drift"] }) {
  if (!drift) {
    return (
      <span className="flex items-center gap-1 text-emerald-400 text-xs">
        <CheckCircle2 className="h-3.5 w-3.5" />
        <span className="sr-only">In sync</span>
      </span>
    );
  }
  if (drift === "price") {
    return (
      <span className="flex items-center gap-1 text-amber-400 text-xs font-medium">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        price
      </span>
    );
  }
  if (drift === "status") {
    return (
      <span className="flex items-center gap-1 text-rose-400 text-xs font-medium">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        status
      </span>
    );
  }
  if (drift === "missing") {
    return (
      <span className="flex items-center gap-1 text-amber-400 text-xs font-medium">
        <PlusCircle className="h-3.5 w-3.5 shrink-0" />
        missing
      </span>
    );
  }
  return null;
}

// ── State badge ───────────────────────────────────────────────────────────────
function StateBadge({ state }: { state: string }) {
  const styles: Record<string, string> = {
    published: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    ready: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    archived: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  };
  return (
    <Badge className={cn("text-[10px] py-0 px-1.5", styles[state] ?? "bg-muted/30 text-muted-foreground")}>
      {state}
    </Badge>
  );
}

// ── Tab types ─────────────────────────────────────────────────────────────────
type TabKey = "all" | "published" | "drafts" | "archived" | "needs_sync" | "unmatched";

function filterItems(items: StoreProduct[], tab: TabKey): StoreProduct[] {
  switch (tab) {
    case "published":
      return items.filter((p) => p.state === "published");
    case "drafts":
      return items.filter((p) => p.state === "wip" || p.state === "draft" || p.shopify_status === "DRAFT");
    case "archived":
      return items.filter((p) => p.state === "archived");
    case "needs_sync":
      return items.filter((p) => p.drift !== null);
    default:
      return items;
  }
}

// columnHelper is stable — defined once outside the component to avoid recreation on every render.
const columnHelper = createColumnHelper<StoreProduct>();

// ── Main page ─────────────────────────────────────────────────────────────────
export function StorePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: productsData, isLoading: loading, error: queryError } = useQuery({
    queryKey: ["store-products"],
    queryFn: () => api.storeProducts(),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
  const items = productsData?.items ?? [];
  const error = queryError ? (queryError instanceof Error ? queryError.message : "Failed to load store products.") : null;
  const fetchProducts = useCallback(() => { qc.invalidateQueries({ queryKey: ["store-products"] }); }, [qc]);
  const [tab, setTab] = useState<TabKey>("all");
  const [, startTransition] = useTransition();
  const shopifyAdminBase = useShopifyAdminURL();
  const [syncing, setSyncing] = useState<Set<string>>(new Set());
  const [pulling, setPulling] = useState<Set<string>>(new Set());
  const [syncingAll, setSyncingAll] = useState(false);
  const [delisting, setDelisting] = useState<Set<string>>(new Set());

  // ── Multi-select state ────────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Clear selection whenever visible set changes (tab switch, refresh)
  useEffect(() => {
    setSelected(new Set());
  }, [tab]);

  const handleSync = useCallback(
    async (id: string) => {
      setSyncing((s) => new Set(s).add(id));
      try {
        await api.storeSync(id);
        await fetchProducts();
      } finally {
        setSyncing((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        });
      }
    },
    [fetchProducts],
  );

  const handlePull = useCallback(
    async (id: string) => {
      setPulling((s) => new Set(s).add(id));
      try {
        await api.storePull(id);
        await fetchProducts();
      } finally {
        setPulling((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        });
      }
    },
    [fetchProducts],
  );

  const handleDelist = useCallback(
    async (id: string) => {
      setDelisting((s) => new Set(s).add(id));
      try {
        await api.patchCuratedState(id, "archived");
        await api.storeSync(id);
        await fetchProducts();
      } finally {
        setDelisting((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        });
      }
    },
    [fetchProducts],
  );

  const handleSyncAll = useCallback(async () => {
    const drifted = items.filter((p) => p.drift !== null);
    if (drifted.length === 0) return;
    setSyncingAll(true);
    // Mark all as in-progress up front so the UI reflects work immediately.
    setSyncing(new Set(drifted.map((p) => p.id)));
    // Run in parallel (max 4 concurrent) — don't serialize N×round-trips.
    const CONCURRENCY = 4;
    const queue = [...drifted];
    const worker = async () => {
      while (queue.length > 0) {
        const p = queue.shift()!;
        try { await api.storeSync(p.id); } catch { /* non-fatal */ }
        setSyncing((s) => { const n = new Set(s); n.delete(p.id); return n; });
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    await fetchProducts();
    setSyncingAll(false);
  }, [items, fetchProducts]);

  // ── Batch actions ─────────────────────────────────────────────────────────
  const handleBatchSync = useCallback(async () => {
    // Parallel batch sync with same 4-worker pool — no serial await chain.
    setSyncing((s) => new Set([...s, ...selected]));
    const ids = [...selected];
    const CONCURRENCY = 4;
    const queue = [...ids];
    const worker = async () => {
      while (queue.length > 0) {
        const id = queue.shift()!;
        try { await api.storeSync(id); } catch { /* non-fatal */ }
        setSyncing((s) => { const n = new Set(s); n.delete(id); return n; });
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    await fetchProducts();
    setSelected(new Set());
  }, [selected, fetchProducts]);

  const handleBatchPull = useCallback(async () => {
    for (const id of selected) {
      await handlePull(id);
    }
    setSelected(new Set());
  }, [selected, handlePull]);

  const handleBatchDelist = useCallback(async () => {
    for (const id of selected) {
      await handleDelist(id);
    }
    setSelected(new Set());
  }, [selected, handleDelist]);

  // ── Selection helpers ─────────────────────────────────────────────────────
  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Memoize all per-tab counts and visible list — recomputes only when items changes,
  // not on every syncing/pulling Set update.
  const tabCounts = useMemo(() => ({
    all: items.length,
    published: items.filter((p) => p.state === "published").length,
    drafts: items.filter((p) => p.state === "wip" || p.state === "draft" || p.shopify_status === "DRAFT").length,
    archived: items.filter((p) => p.state === "archived").length,
    needs_sync: items.filter((p) => p.drift !== null).length,
    unmatched: 0,
  }), [items]);
  const driftCount = tabCounts.needs_sync;
  const visible = useMemo(() => filterItems(items, tab), [items, tab]);
  const tabCount = (key: TabKey) => tabCounts[key] ?? 0;

  // ── TanStack Table — sorting + global filter ──────────────────────────────
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters] = useState<ColumnFiltersState>([]);

  const columns = useMemo(() => [
    columnHelper.accessor("title", { id: "title", enableSorting: true }),
    columnHelper.accessor("brand_slug", { id: "brand_slug", enableSorting: true }),
    columnHelper.accessor(
      (row) => (row.local_price ? parseFloat(row.local_price) : 0),
      { id: "local_price", enableSorting: true },
    ),
    columnHelper.accessor(
      (row) => (row.shopify_price ? parseFloat(row.shopify_price) : 0),
      { id: "shopify_price", enableSorting: true },
    ),
    columnHelper.accessor("qty_on_hand", { id: "qty_on_hand", enableSorting: true }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const table = useReactTable<StoreProduct>({
    data: visible,
    columns,
    state: { sorting, columnFilters, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, _colId, filterValue: string) => {
      const q = filterValue.toLowerCase();
      return (
        (row.original.title ?? "").toLowerCase().includes(q) ||
        (row.original.tdsynnex_sku ?? "").toLowerCase().includes(q)
      );
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const tableRows = table.getRowModel().rows;
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 52,
    overscan: 10,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom = virtualRows.length > 0
    ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
    : 0;

  const allVisibleSelected =
    tableRows.length > 0 && tableRows.every((r) => selected.has(r.original.id));
  const someVisibleSelected =
    tableRows.some((r) => selected.has(r.original.id)) && !allVisibleSelected;

  const toggleSelectAll = useCallback(() => {
    if (allVisibleSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        tableRows.forEach((r) => next.delete(r.original.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        tableRows.forEach((r) => next.add(r.original.id));
        return next;
      });
    }
  }, [allVisibleSelected, tableRows]);

  const [unmatched, setUnmatched] = useState<ShopifyCatalogProduct[]>([]);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  const [claiming, setClaiming] = useState<Set<string>>(new Set());
  const [unmatchedActioning, setUnmatchedActioning] = useState<Set<string>>(new Set());

  const fetchUnmatched = useCallback(async () => {
    setUnmatchedLoading(true);
    try {
      const res = await api.shopifyCatalog();
      setUnmatched((res.products ?? []).filter((p) => p.local_id === null));
    } catch { /* ignore */ } finally {
      setUnmatchedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnmatched();
  }, [fetchUnmatched]);

  const batchBusy = syncing.size > 0 || pulling.size > 0 || delisting.size > 0;
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ linked: number; unmatched: number } | null>(null);
  const [pullingAll, setPullingAll] = useState(false);
  const [syncingImages, setSyncingImages] = useState(false);

  const handleBulkImageSync = useCallback(async () => {
    setSyncingImages(true);
    try {
      const res = await api.bulkImageSync();
      toast.success(`Images synced: ${res.synced} products updated, ${res.skipped} skipped`);
      qc.invalidateQueries({ queryKey: ["store-products"] });
    } catch (e) {
      toast.error("Image sync failed: " + (e instanceof Error ? e.message : "unknown"));
    } finally {
      setSyncingImages(false);
    }
  }, [qc]);

  const handlePullAll = useCallback(async () => {
    setPullingAll(true);
    try {
      const res = await api.pullAllFromShopify();
      toast.success(`Pulled from Shopify: ${res.matched} matched, ${res.missing} missing`);
      fetchProducts();
      fetchUnmatched();
    } catch (e) {
      toast.error("Pull failed: " + (e instanceof Error ? e.message : "unknown"));
    } finally {
      setPullingAll(false);
    }
  }, [fetchProducts, fetchUnmatched]);
  const [conflicts, setConflicts] = useState<ImportConflict[]>([]);
  // Per-conflict resolution: curated_id → "shopify" | "local"
  const [resolutions, setResolutions] = useState<Record<string, "shopify" | "local">>({});
  const [resolving, setResolving] = useState(false);

  const handleImport = useCallback(async () => {
    setImporting(true);
    setImportResult(null);
    setConflicts([]);
    setResolutions({});
    try {
      const res = await api.storeImport();
      setImportResult({ linked: res.linked, unmatched: res.unmatched });
      if (res.conflicts.length > 0) {
        setConflicts(res.conflicts);
        // Default: keep local for all conflicts.
        const defaults: Record<string, "shopify" | "local"> = {};
        res.conflicts.forEach((c) => { defaults[c.curated_id] = "local"; });
        setResolutions(defaults);
      }
      await fetchProducts();
    } finally {
      setImporting(false);
    }
  }, [fetchProducts]);

  const handleResolve = useCallback(async () => {
    setResolving(true);
    try {
      await api.storeImportResolve(
        conflicts.map((c) => ({
          curated_id: c.curated_id,
          shopify_product_id: c.shopify_product_id,
          shopify_price: c.shopify_price,
          shopify_status: c.shopify_status,
          keep: resolutions[c.curated_id] ?? "local",
        }))
      );
      setConflicts([]);
      setResolutions({});
      await fetchProducts();
    } finally {
      setResolving(false);
    }
  }, [conflicts, resolutions, fetchProducts]);

  return (
    <div className="flex flex-col gap-5">
      {/* ── Header ── */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Store</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Local mirror of published Shopify listings — drift-detected against live state.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate("/store/shopify-catalog")}
            className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-slate-200"
          >
            <LayoutList className="h-3.5 w-3.5" />
            Shopify Catalog
          </Button>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handlePullAll}
                  disabled={pullingAll || loading}
                  className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-slate-200"
                >
                  <CloudDownload className={cn("h-3.5 w-3.5", pullingAll && "animate-bounce")} />
                  {pullingAll ? "Pulling…" : "Pull from Shopify"}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[240px] text-[11px] leading-relaxed">
                Re-sync local records from Shopify by MPN. Fixes stale IDs without overwriting your pricing or descriptions.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleBulkImageSync}
                  disabled={syncingImages || loading}
                  className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-slate-200"
                >
                  <ImageOff className={cn("h-3.5 w-3.5", syncingImages && "animate-spin")} />
                  {syncingImages ? "Syncing images…" : "Sync Images"}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[240px] text-[11px] leading-relaxed">
                Pull product images from Shopify for all local records. Replaces any previously synced images.
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleImport}
                  disabled={importing || loading}
                  className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-slate-200"
                >
                  <ArrowDownToLine className={cn("h-3.5 w-3.5", importing && "animate-bounce")} />
                  {importing ? "Importing…" : "← Import from Shopify"}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[220px] text-[11px] leading-relaxed">
                Scan all Shopify products and link them to local records by SKU. Use on first production connection.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchProducts}
            disabled={loading || syncingAll}
            className="h-8 gap-1.5 text-xs"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            Refresh
          </Button>
          {driftCount > 0 && (
            <Button
              size="sm"
              onClick={handleSyncAll}
              disabled={syncingAll || loading}
              className="h-8 gap-1.5 text-xs bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30"
            >
              <ArrowUpFromLine className="h-3.5 w-3.5" />
              {syncingAll ? "Syncing…" : `Sync All → (${driftCount})`}
            </Button>
          )}
        </div>
      </div>

      {/* ── Sync health strip + bulk sync ── */}
      <SyncHealthStrip />

      {/* ── Import result banner ── */}
      {importResult && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">
          <ArrowDownToLine className="h-4 w-4 shrink-0" />
          <span>
            Import complete — <strong>{importResult.linked}</strong> linked
            {importResult.unmatched > 0 && <>, <span className="text-amber-400">{importResult.unmatched} unmatched</span></>}
            {conflicts.length > 0 && <>, <span className="text-amber-300">{conflicts.length} conflict{conflicts.length !== 1 ? "s" : ""} need review below</span></>}.
          </span>
          <button onClick={() => setImportResult(null)} className="ml-auto text-emerald-400/60 hover:text-emerald-200">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* ── Import conflicts panel ── */}
      {conflicts.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-amber-300">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="font-medium">{conflicts.length} product{conflicts.length !== 1 ? "s" : ""} with conflicting data — choose which version to keep</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setResolutions(Object.fromEntries(conflicts.map((c) => [c.curated_id, "local" as const])))}
                className="text-xs text-muted-foreground hover:text-slate-200 underline underline-offset-2"
              >Keep all local</button>
              <span className="text-muted-foreground text-xs">·</span>
              <button
                onClick={() => setResolutions(Object.fromEntries(conflicts.map((c) => [c.curated_id, "shopify" as const])))}
                className="text-xs text-muted-foreground hover:text-slate-200 underline underline-offset-2"
              >Use all Shopify</button>
            </div>
          </div>

          <div className="space-y-2">
            {conflicts.map((c) => {
              const keep = resolutions[c.curated_id] ?? "local";
              return (
                <div key={c.curated_id} className="flex items-center gap-3 rounded-md border border-border bg-muted/20 px-3 py-2.5 text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-slate-200">{c.title}</p>
                    <p className="text-xs text-muted-foreground">{c.tdsynnex_sku}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs shrink-0">
                    <div className="text-center">
                      <div className="text-muted-foreground mb-0.5">Local</div>
                      <div className="text-slate-300">${c.local_price} · {c.local_state}</div>
                    </div>
                    <div className="text-muted-foreground">vs</div>
                    <div className="text-center">
                      <div className="text-muted-foreground mb-0.5">Shopify</div>
                      <div className="text-slate-300">${c.shopify_price} · {c.shopify_status}</div>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => setResolutions((r) => ({ ...r, [c.curated_id]: "local" }))}
                      className={cn(
                        "px-2 py-1 rounded text-xs font-medium transition-colors",
                        keep === "local"
                          ? "bg-slate-600 text-slate-100"
                          : "text-muted-foreground hover:text-slate-300"
                      )}
                    >Keep local</button>
                    <button
                      onClick={() => setResolutions((r) => ({ ...r, [c.curated_id]: "shopify" }))}
                      className={cn(
                        "px-2 py-1 rounded text-xs font-medium transition-colors",
                        keep === "shopify"
                          ? "bg-amber-600 text-amber-50"
                          : "text-muted-foreground hover:text-slate-300"
                      )}
                    >Use Shopify</button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end pt-1">
            <Button size="sm" variant="ember" onClick={handleResolve} disabled={resolving}>
              {resolving ? "Applying…" : `Apply ${conflicts.length} resolution${conflicts.length !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      )}

      {/* ── Drift banner ── */}
      {driftCount > 0 && !loading && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            <strong className="font-semibold">{driftCount}</strong>{" "}
            {driftCount === 1 ? "product" : "products"} out of sync with Shopify.
          </span>
          <button
            onClick={() => startTransition(() => setTab("needs_sync"))}
            className="ml-auto underline underline-offset-2 text-amber-400 hover:text-amber-200 text-xs font-medium"
          >
            Review
          </button>
        </div>
      )}

      {/* ── Search + Tabs row ── */}
      <div className="flex items-center gap-3">
        <input
          type="search"
          placeholder="Search title or SKU…"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="bg-muted/30 border border-border text-sm rounded-md px-2 py-1 text-slate-200 placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/40 w-48 shrink-0"
        />
        <Tabs value={tab} onValueChange={(v) => { startTransition(() => { setTab(v as TabKey); setGlobalFilter(""); }); }} className="flex-1">
        <TabsList className="bg-muted/30">
          <TabsTrigger value="all">All ({tabCount("all")})</TabsTrigger>
          <TabsTrigger value="published">Published ({tabCount("published")})</TabsTrigger>
          <TabsTrigger value="drafts">Drafts ({tabCount("drafts")})</TabsTrigger>
          <TabsTrigger value="archived">Archived ({tabCount("archived")})</TabsTrigger>
          <TabsTrigger value="needs_sync">
            Needs Sync
            {tabCount("needs_sync") > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500/30 px-1.5 py-px text-[10px] text-amber-300 font-semibold">
                {tabCount("needs_sync")}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="unmatched">
            Unmatched
            {unmatched.length > 0 && (
              <span className="ml-1.5 rounded-full bg-rose-500/30 px-1.5 py-px text-[10px] text-rose-300 font-semibold">
                {unmatched.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        </Tabs>
      </div>

      {/* ── Table container (position relative so sticky toolbar anchors here) ── */}
      <div className="relative">
        <div
          ref={tableContainerRef}
          className={cn(
            "rounded-lg border border-border/60 overflow-auto transition-opacity",
            loading && "opacity-60",
            tab === "unmatched" && "hidden",
          )}
          style={{ maxHeight: "calc(100vh - 280px)" }}
        >
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {/* Checkbox column */}
                <TableHead className="py-2 w-8 pl-3 pr-1 shrink-0">
                  <Checkbox
                    checked={allVisibleSelected || (someVisibleSelected ? "indeterminate" : false)}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all"
                    disabled={loading || visible.length === 0}
                  />
                </TableHead>
                {/* Product column takes all remaining space; others are fixed-width */}
                <TableHead className="py-2 w-full min-w-0">
                  <button
                    onClick={() => table.getColumn("title")?.toggleSorting()}
                    className="flex items-center gap-1 uppercase text-[10px] font-medium tracking-wide hover:text-slate-200 transition-colors"
                  >
                    Product
                    <span className="text-[10px] text-muted-foreground/60">
                      {table.getColumn("title")?.getIsSorted() === "asc" ? "↑" : table.getColumn("title")?.getIsSorted() === "desc" ? "↓" : ""}
                    </span>
                  </button>
                </TableHead>
                <TableHead className="py-2 w-24 whitespace-nowrap">
                  <button
                    onClick={() => table.getColumn("brand_slug")?.toggleSorting()}
                    className="flex items-center gap-1 uppercase text-[10px] font-medium tracking-wide hover:text-slate-200 transition-colors"
                  >
                    Brand
                    <span className="text-[10px] text-muted-foreground/60">
                      {table.getColumn("brand_slug")?.getIsSorted() === "asc" ? "↑" : table.getColumn("brand_slug")?.getIsSorted() === "desc" ? "↓" : ""}
                    </span>
                  </button>
                </TableHead>
                <TableHead className="py-2 w-24 whitespace-nowrap">
                  <span className="uppercase text-[10px] font-medium tracking-wide">State</span>
                </TableHead>
                <TableHead className="py-2 w-28 whitespace-nowrap tabular-nums">
                  <button
                    onClick={() => table.getColumn("local_price")?.toggleSorting()}
                    className="flex items-center gap-1 uppercase text-[10px] font-medium tracking-wide hover:text-slate-200 transition-colors"
                  >
                    Local Price
                    <span className="text-[10px] text-muted-foreground/60">
                      {table.getColumn("local_price")?.getIsSorted() === "asc" ? "↑" : table.getColumn("local_price")?.getIsSorted() === "desc" ? "↓" : ""}
                    </span>
                  </button>
                </TableHead>
                <TableHead className="py-2 w-28 whitespace-nowrap tabular-nums">
                  <button
                    onClick={() => table.getColumn("shopify_price")?.toggleSorting()}
                    className="flex items-center gap-1 uppercase text-[10px] font-medium tracking-wide hover:text-slate-200 transition-colors"
                  >
                    Shopify Price
                    <span className="text-[10px] text-muted-foreground/60">
                      {table.getColumn("shopify_price")?.getIsSorted() === "asc" ? "↑" : table.getColumn("shopify_price")?.getIsSorted() === "desc" ? "↓" : ""}
                    </span>
                  </button>
                </TableHead>
                <TableHead className="py-2 w-28 whitespace-nowrap">
                  <span className="uppercase text-[10px] font-medium tracking-wide">Shopify Status</span>
                </TableHead>
                <TableHead className="py-2 w-24 whitespace-nowrap tabular-nums">
                  <button
                    onClick={() => table.getColumn("qty_on_hand")?.toggleSorting()}
                    className="flex items-center gap-1 uppercase text-[10px] font-medium tracking-wide hover:text-slate-200 transition-colors"
                  >
                    Inventory
                    <span className="text-[10px] text-muted-foreground/60">
                      {table.getColumn("qty_on_hand")?.getIsSorted() === "asc" ? "↑" : table.getColumn("qty_on_hand")?.getIsSorted() === "desc" ? "↓" : ""}
                    </span>
                  </button>
                </TableHead>
                <TableHead className="py-2 w-20 whitespace-nowrap">
                  <span className="uppercase text-[10px] font-medium tracking-wide">Drift</span>
                </TableHead>
                <TableHead className="py-2 w-28 whitespace-nowrap">
                  <span className="uppercase text-[10px] font-medium tracking-wide">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 10 }).map((_, j) => (
                      <TableCell key={j}>
                        <div className="h-4 rounded shimmer" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : error ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center text-destructive py-16 text-sm"
                  >
                    {error}
                  </TableCell>
                </TableRow>
              ) : tableRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center text-muted-foreground py-16 text-sm"
                  >
                    {globalFilter ? "No products match your search." : "No products in this view."}
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {paddingTop > 0 && <tr><td style={{ height: paddingTop }} colSpan={10} /></tr>}
                  {virtualRows.map((virtualItem) => {
                  const row = tableRows[virtualItem.index];
                  const product = row.original;
                  const isSyncing = syncing.has(product.id);
                  const isPulling = pulling.has(product.id);
                  const isDelisting = delisting.has(product.id);
                  const isSelected = selected.has(product.id);
                  const hasPriceDrift = product.drift === "price";
                  const shopifyAdminURL = shopifyAdminBase && product.shopify_product_id
                    ? `${shopifyAdminBase}/products/${product.shopify_product_id}`
                    : null;

                  return (
                    <TableRow
                      key={product.id}
                      className={cn(
                        "hover:bg-muted/20",
                        isSelected && "bg-emerald-500/5 hover:bg-emerald-500/10",
                      )}
                    >
                      {/* Checkbox */}
                      <TableCell className="py-2 pl-3 pr-1 w-8">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleRow(product.id)}
                          aria-label={`Select ${product.title}`}
                        />
                      </TableCell>

                      {/* Product title + SKU — takes all remaining width, truncates */}
                      <TableCell className="py-2 w-full min-w-0 max-w-0">
                        <button
                          onClick={() => navigate(`/curated/${product.id}`)}
                          className="text-left hover:text-emerald-400 transition-colors group w-full"
                        >
                          <div className="font-medium text-sm leading-tight truncate group-hover:underline underline-offset-2" title={product.title ?? ""}>
                            {product.title}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-muted-foreground/70 font-mono text-[10px] tabular-nums">
                              {product.tdsynnex_sku}
                            </span>
                            {product.image_count === 0 && (
                              <span className="flex items-center gap-0.5 text-[10px] text-rose-400 font-medium">
                                <ImageOff className="h-3 w-3" />no img
                              </span>
                            )}
                          </div>
                        </button>
                      </TableCell>

                      {/* Brand */}
                      <TableCell className="py-2 whitespace-nowrap">
                        <Badge variant="outline" className="capitalize text-[10px] py-0">
                          {product.brand_slug || "—"}
                        </Badge>
                      </TableCell>

                      {/* Local state */}
                      <TableCell className="py-2 whitespace-nowrap">
                        <StateBadge state={product.state} />
                      </TableCell>

                      {/* Local price */}
                      <TableCell className="py-2 whitespace-nowrap font-mono tabular-nums text-xs">
                        {product.local_price ? formatUsd(parseFloat(product.local_price)) : "—"}
                      </TableCell>

                      {/* Shopify price with drift icon */}
                      <TableCell className="py-2 whitespace-nowrap font-mono tabular-nums text-xs">
                        <span
                          className={cn(
                            "flex items-center gap-1",
                            hasPriceDrift && "text-amber-400",
                          )}
                        >
                          {product.shopify_price
                            ? formatUsd(parseFloat(product.shopify_price))
                            : <span className="text-muted-foreground/40">—</span>
                          }
                          {hasPriceDrift && (
                            <AlertTriangle className="h-3 w-3 shrink-0" />
                          )}
                        </span>
                      </TableCell>

                      {/* Shopify status */}
                      <TableCell className="py-2 whitespace-nowrap">
                        <ShopifyStatusBadge status={product.shopify_status} />
                      </TableCell>

                      {/* Inventory */}
                      <TableCell className="py-2 whitespace-nowrap font-mono tabular-nums text-xs">
                        <span
                          className={cn(
                            product.qty_on_hand === 0 && "text-rose-400",
                            product.qty_on_hand > 0 && product.qty_on_hand <= 5 && "text-amber-400",
                            product.qty_on_hand > 5 && "text-emerald-400",
                          )}
                        >
                          {formatInt(product.qty_on_hand)}
                        </span>
                      </TableCell>

                      {/* Drift */}
                      <TableCell className="py-2 whitespace-nowrap">
                        <DriftIndicator drift={product.drift} />
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="py-2 whitespace-nowrap">
                        <TooltipProvider delayDuration={300}>
                          <div className="flex items-center gap-0.5">
                            {/* Edit */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(`/curated/${product.id}`)}
                                  className="h-6 w-6 p-0 text-muted-foreground/60 hover:text-slate-200"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-[11px]">Edit listing</TooltipContent>
                            </Tooltip>

                            {/* Sync → to Shopify — always visible; amber when drift, muted when in sync */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleSync(product.id)}
                                  disabled={isSyncing || syncingAll || isDelisting}
                                  className={cn(
                                    "h-6 w-6 p-0 transition-colors",
                                    product.drift !== null
                                      ? "text-amber-400 hover:text-amber-300"
                                      : "text-muted-foreground/40 hover:text-slate-300",
                                  )}
                                >
                                  <ArrowUpFromLine className={cn("h-3 w-3", isSyncing && "animate-bounce")} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-[11px]">
                                {product.drift !== null
                                  ? `Sync → to Shopify (${product.drift} drift)`
                                  : "Sync → to Shopify"}
                              </TooltipContent>
                            </Tooltip>

                            {/* ← Pull from Shopify — always visible; muted when no shopify_product_id */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handlePull(product.id)}
                                  disabled={isPulling || isSyncing || isDelisting || !product.shopify_product_id}
                                  className={cn(
                                    "h-6 w-6 p-0 transition-colors",
                                    product.shopify_product_id
                                      ? "text-slate-400 hover:text-slate-200"
                                      : "text-muted-foreground/20 cursor-not-allowed",
                                  )}
                                >
                                  <ArrowDownToLine className={cn("h-3 w-3", isPulling && "animate-bounce")} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-[11px]">
                                {product.shopify_product_id
                                  ? "← Pull from Shopify"
                                  : "Not on Shopify yet"}
                              </TooltipContent>
                            </Tooltip>

                            {/* Delist (published only) */}
                            {product.state === "published" && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDelist(product.id)}
                                    disabled={isDelisting || isSyncing}
                                    className="h-6 w-6 p-0 text-muted-foreground/60 hover:text-rose-400"
                                  >
                                    <EyeOff className={cn("h-3 w-3", isDelisting && "animate-pulse")} />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-[11px]">
                                  Delist — archives locally and on Shopify
                                </TooltipContent>
                              </Tooltip>
                            )}

                            {/* View in Shopify Admin */}
                            {shopifyAdminURL && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <a
                                    href={shopifyAdminURL}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center h-6 w-6 rounded text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-[11px]">View in Shopify Admin</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  );
                  })}
                  {paddingBottom > 0 && <tr><td style={{ height: paddingBottom }} colSpan={10} /></tr>}
                </>
              )}
            </TableBody>
          </Table>
        </div>

        {/* ── Unmatched Shopify products ── */}
        {tab === "unmatched" && (
          <TooltipProvider delayDuration={300}>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground px-1">
              These products exist in Shopify but have no Symphony record. <strong>Claim</strong> them to bring them under management — no TD SYNNEX SKU required yet.
            </p>
            <div className="rounded-lg border border-border/60 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/40 bg-muted/20">
                    <TableHead className="py-2 text-[11px] font-medium tracking-wide uppercase text-muted-foreground/70">Shopify Product</TableHead>
                    <TableHead className="py-2 text-[11px] font-medium tracking-wide uppercase text-muted-foreground/70">Status</TableHead>
                    <TableHead className="py-2 text-[11px] font-medium tracking-wide uppercase text-muted-foreground/70">Price</TableHead>
                    <TableHead className="py-2 text-[11px] font-medium tracking-wide uppercase text-muted-foreground/70">Shopify</TableHead>
                    <TableHead className="py-2 text-[11px] font-medium tracking-wide uppercase text-muted-foreground/70">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unmatchedLoading && (
                    <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                  )}
                  {!unmatchedLoading && unmatched.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No unmatched Shopify products — all linked.</TableCell></TableRow>
                  )}
                  {unmatched.map((p) => {
                    const numID = p.shopify_id.replace("gid://shopify/Product/", "");
                    const isClaiming = claiming.has(p.shopify_id);
                    return (
                      <TableRow key={p.shopify_id} className={cn("border-border/30 hover:bg-muted/10", p.is_duplicate && "bg-amber-500/5")}>
                        <TableCell className="py-2">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium">{p.title}</span>
                            <div className="flex items-center gap-1.5">
                              {p.variant_sku && <span className="text-[10px] font-mono text-muted-foreground">{p.variant_sku}</span>}
                              {p.is_duplicate && (
                                <span className="flex items-center gap-0.5 text-[10px] text-amber-400 font-medium">
                                  <Copy className="h-3 w-3" />duplicate MPN
                                </span>
                              )}
                              {!p.has_image && (
                                <span className="flex items-center gap-0.5 text-[10px] text-rose-400 font-medium">
                                  <ImageOff className="h-3 w-3" />no image
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-2">
                          <ShopifyStatusBadge status={p.shopify_status} />
                        </TableCell>
                        <TableCell className="py-2 font-mono tabular-nums text-sm">{formatUsd(Number(p.shopify_price))}</TableCell>
                        <TableCell className="py-2">
                          <a
                            href={shopifyAdminBase ? `${shopifyAdminBase}/products/${numID}` : "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {numID}
                          </a>
                        </TableCell>
                        <TableCell className="py-2">
                          <div className="flex items-center gap-1">
                            {/* Claim → brings into Symphony */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm" variant="outline"
                                  disabled={isClaiming || unmatchedActioning.has(p.shopify_id)}
                                  className="h-6 text-xs gap-1 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                                  onClick={async () => {
                                    setClaiming((prev) => new Set(prev).add(p.shopify_id));
                                    try {
                                      const res = await api.claimShopifyProduct(p.shopify_id);
                                      setUnmatched((prev) => prev.filter((u) => u.shopify_id !== p.shopify_id));
                                      navigate(`/curated/${res.id}`);
                                    } catch (e) {
                                      toast.error("Claim failed: " + (e instanceof Error ? e.message : "unknown error"));
                                    } finally {
                                      setClaiming((prev) => { const s = new Set(prev); s.delete(p.shopify_id); return s; });
                                    }
                                  }}
                                >
                                  {isClaiming ? <RefreshCw className="h-3 w-3 animate-spin" /> : <PlusCircle className="h-3 w-3" />}
                                  Claim
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-[11px]">Import into Symphony to manage locally</TooltipContent>
                            </Tooltip>

                            {/* → Draft in Shopify */}
                            {p.shopify_status !== "DRAFT" && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm" variant="ghost"
                                    disabled={unmatchedActioning.has(p.shopify_id) || isClaiming}
                                    className="h-6 w-6 p-0 text-slate-400 hover:text-slate-200"
                                    onClick={async () => {
                                      setUnmatchedActioning((prev) => new Set(prev).add(p.shopify_id));
                                      try {
                                        await api.unmatchedSetStatus(p.shopify_id, "DRAFT");
                                        setUnmatched((prev) => prev.map((u) => u.shopify_id === p.shopify_id ? { ...u, shopify_status: "DRAFT" } : u));
                                        toast.success("Set to Draft in Shopify");
                                      } catch (e) {
                                        toast.error("Failed: " + (e instanceof Error ? e.message : "error"));
                                      } finally {
                                        setUnmatchedActioning((prev) => { const s = new Set(prev); s.delete(p.shopify_id); return s; });
                                      }
                                    }}
                                  >
                                    <ArrowDownToLine className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-[11px]">Set Draft in Shopify</TooltipContent>
                              </Tooltip>
                            )}

                            {/* → Archive in Shopify */}
                            {p.shopify_status !== "ARCHIVED" && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm" variant="ghost"
                                    disabled={unmatchedActioning.has(p.shopify_id) || isClaiming}
                                    className="h-6 w-6 p-0 text-rose-400/70 hover:text-rose-300"
                                    onClick={async () => {
                                      setUnmatchedActioning((prev) => new Set(prev).add(p.shopify_id));
                                      try {
                                        await api.unmatchedSetStatus(p.shopify_id, "ARCHIVED");
                                        setUnmatched((prev) => prev.map((u) => u.shopify_id === p.shopify_id ? { ...u, shopify_status: "ARCHIVED" } : u));
                                        toast.success("Archived in Shopify");
                                      } catch (e) {
                                        toast.error("Failed: " + (e instanceof Error ? e.message : "error"));
                                      } finally {
                                        setUnmatchedActioning((prev) => { const s = new Set(prev); s.delete(p.shopify_id); return s; });
                                      }
                                    }}
                                  >
                                    <EyeOff className="h-3 w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-[11px]">Archive in Shopify</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          </TooltipProvider>
        )}

        {/* ── Floating batch action toolbar ── */}
        {selected.size > 0 && (
          <div className="sticky bottom-0 mt-0 z-10 flex items-center gap-2 px-3 py-2 rounded-b-lg border-t border-emerald-500/20 bg-slate-900/90 backdrop-blur-sm">
            <span className="text-xs font-medium text-emerald-400 tabular-nums mr-1">
              {selected.size} selected
            </span>

            <Button
              size="sm"
              disabled={batchBusy}
              onClick={handleBatchSync}
              className="h-7 gap-1.5 text-xs bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30"
            >
              <ArrowUpFromLine className="h-3 w-3" />
              → Sync to Shopify
            </Button>

            <Button
              size="sm"
              disabled={batchBusy}
              onClick={handleBatchPull}
              className="h-7 gap-1.5 text-xs bg-slate-500/20 border border-slate-500/40 text-slate-300 hover:bg-slate-500/30"
            >
              <ArrowDownToLine className="h-3 w-3" />
              ← Pull from Shopify
            </Button>

            <Button
              size="sm"
              disabled={batchBusy}
              onClick={handleBatchDelist}
              className="h-7 gap-1.5 text-xs bg-rose-500/20 border border-rose-500/40 text-rose-300 hover:bg-rose-500/30"
            >
              <EyeOff className="h-3 w-3" />
              Delist
            </Button>

            <button
              onClick={() => setSelected(new Set())}
              className="ml-auto text-xs text-muted-foreground hover:text-slate-200 flex items-center gap-1 transition-colors"
            >
              <X className="h-3 w-3" />
              Clear
            </button>
          </div>
        )}
      </div>

      {/* ── Footer count ── */}
      {!loading && !error && (
        <div className="text-xs text-muted-foreground tabular-nums">
          {formatInt(tableRows.length)} of {formatInt(items.length)} listings{globalFilter && tableRows.length !== visible.length && ` (${visible.length} in tab)`}
          {selected.size > 0 && (
            <span className="ml-2 text-emerald-400">· {selected.size} selected</span>
          )}
        </div>
      )}
    </div>
  );
}
